import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { take } from 'rxjs';
import { ModalTimeAvailabilityComponent } from '@base/components/private/modals/modal-time-availability/modal-time-availability.component';
import { LoadMFESrvAbstract } from '@base/abstracts/services/load-mfe.abstract';
import { defaultModalConfig } from '@base/constants/common';
import { TypeOfDecline } from '@base/enums/common.enum';
import { TypeOfRequestLabel } from '@base/enums/request-label.enum';
import { ModalConfig } from '@base/models/others/modal-config';
import { GlobalService } from '@base/services/private/global.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { ModalService } from '@base/services/private/modal.service';
import { RequestLabelService } from '@base/services/private/request-label.service';
import { SessionService } from '@base/services/private/session.service';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { getMfeAttributes, loadWidget } from '@base/utils';
import { environment } from 'environments/environment';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { CookieService } from 'ngx-cookie-service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SupportedLanguages } from '@config/supported-languages.enum';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { FaroService } from '@core/services/faro.service';
import {
  BUSINESS_MATCHING_ACTION_MAP,
  BusinessMatchingValidationErrorCode,
  BUSINESS_MATCHING_VALIDATION_ERROR_DESCRIPTION_MAP,
  BUSINESS_MATCHING_VALIDATION_ERROR_TITLE_MAP,
} from '@base/models/others/business-matching.constants';
import {
  BusinessMatchingListenerEvent,
  BusinessMatchingListenerEventStatus,
  BusinessMatchingMeetingStatus,
  BusinessMatchingCtaAction,
  BusinessMatchingDeclineStatus,
  BusinessMatchingMeetingType,
} from '@base/models/others/business-matching.models';
import { PlannerGuidanceContext, PlannerGuidanceService } from '@base/services/private/planner-guidance.service';

@Directive()
export abstract class BusinessMatchingAbstract implements AfterViewInit, OnDestroy {
  readonly businessMatchingContainer = viewChild<ElementRef>('businessMatchingContainer');

  readonly peopleId = input('');
  readonly modalId = input('');
  readonly action = input<TypeOfRequestLabel>(TypeOfRequestLabel.REQUEST_MEETING);
  readonly isReschedule = input(false);
  readonly isMeetFromChat = input(false);
  // Dead inputs: modal.service passes these to all modal components generically.
  readonly isEdit = input(false);
  readonly isSkipCheckMeeting = input(false);
  // When false (Informa MFE context), close() dismisses only this modal so parent Informa modals stay open.
  readonly isCloseAll = input(true);

  readonly meetingRequestCompleted = output<void>();

  private businessMatchingElement: HTMLElement | null = null;
  private readonly successScreenEvent = signal<BusinessMatchingListenerEvent | null>(null);
  private readonly globalSrv = inject(GlobalService);
  private readonly globalStateSrv = inject(GlobalStateService);
  protected readonly isShowCloseBtn = !this.globalStateSrv.isExternalMode();
  readonly loadState = signal<'loading' | 'loaded' | 'failed'>('loading');
  protected readonly modalSrv = inject(ModalService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly requestLabelSrv = inject(RequestLabelService);
  private readonly sessionSrv = inject(SessionService);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly faroSrv = inject(FaroService);
  private readonly cookieSrv = inject(CookieService);
  private readonly translatePipe = inject(TranslatePipe);
  private loadMfeSrv: LoadMFESrvAbstract | null = null;
  private readonly plannerGuidanceSrv = inject(PlannerGuidanceService);

  /**
   * Skip script load when the custom element is already registered (e.g. another BM
   * modal opened earlier in the same session). Otherwise kick off the two-attempt load.
   */
  ngAfterViewInit(): void {
    if (customElements.get('jublia-business-matching-module')) {
      void this.initBusinessMatchingWithMfeService();
      return;
    }
    void this.tryLoad(1);
  }

  /** Fetches and executes the MFE bundle via `loadWidget`. */
  loadMfeScript(): Promise<void> {
    return loadWidget(environment.jubliaBusinessMatching, 'jublia-business-matching-module');
  }

  /**
   * Attempt to load the MFE script; retries once on failure before marking `loadState` as `failed`.
   * Max two attempts to avoid an infinite retry loop on persistent network errors.
   */
  private async tryLoad(attempt: 1 | 2): Promise<void> {
    try {
      await this.loadMfeScript();
      await this.initBusinessMatchingWithMfeService();
    } catch {
      if (attempt === 1) {
        void this.tryLoad(2);
      } else {
        this.loadState.set('failed');
      }
    }
  }

  /**
   * Called from the error template when the initial load fails.
   * Re-uses the already-registered element if present; otherwise triggers a fresh two-attempt load.
   */
  async retryLoad(): Promise<void> {
    if (customElements.get('jublia-business-matching-module')) {
      await this.initBusinessMatchingWithMfeService();
      return;
    }
    this.loadState.set('loading');
    await this.tryLoad(1);
  }

  private async initBusinessMatchingWithMfeService(): Promise<void> {
    try {
      this.loadMfeSrv = await this.dynamicImportLoaderSrv.loadService<LoadMFESrvAbstract>(WhiteLabel.SERVICES.LoadMfe);
    } catch (error) {
      console.error('Failed to load LoadMfeService for Business Matching:', error);
    }

    this.initBusinessMatching();
    this.loadState.set('loaded');
  }

  /** Remove the custom element from the DOM on modal teardown. */
  ngOnDestroy(): void {
    this.destroyBusinessMatching();
  }

  /** Destroy the MFE element and close modal(s) via `doClose`. */
  close(): void {
    this.successScreenEvent.set(null);
    this.destroyBusinessMatching();
    this.doClose();
  }

  /** The host X acknowledges a completed action just like the MFE Done button. */
  dismiss(): void {
    const success = this.successScreenEvent();
    if (!success) {
      this.close();
      return;
    }
    this.successScreenEvent.set(null);
    this.onBusinessMatchingEvent(
      new CustomEvent<BusinessMatchingListenerEvent>('businessMatchingListener', {
        detail: { ...success, isFromSuccessScreen: true },
      })
    );
  }

  /** Closes only this modal or all modals depending on `isCloseAll` input. */
  protected doClose(): void {
    if (this.isCloseAll()) {
      this.modalSrv.closeAll();
    } else {
      this.modalSrv.close(this.modalId());
    }
  }

  /**
   * Creates and mounts the `<jublia-business-matching-module>` custom element with all required
   * attributes sourced from global state and component inputs. Destroys any prior instance
   * first to avoid duplicates. No-ops if global state is not yet resolved.
   */
  private initBusinessMatching(): void {
    this.destroyBusinessMatching();
    const state = this.globalStateSrv.getState();
    if (!state?.validate || !state?.loader) return;

    const { event_background, person_lang, event_color } = state.validate;
    const { event_hash, event_phase } = state.loader;
    const isAnon = this.globalStateSrv.isAnon();
    const localLang = this.sessionSrv.getItem('lang') as string;
    const customSession = this.cookieSrv.get('custom-session');

    const mfeAction = this.isReschedule() ? 'RESCHEDULE' : (BUSINESS_MATCHING_ACTION_MAP[this.action()] ?? 'REQUEST');

    this.businessMatchingElement = document.createElement('jublia-business-matching-module');

    this.businessMatchingElement.setAttribute('event-hash', event_hash);
    this.businessMatchingElement.setAttribute('event-color', event_color);
    this.businessMatchingElement.setAttribute('event-background', event_background);
    this.businessMatchingElement.setAttribute('lang', (isAnon ? localLang : person_lang) || SupportedLanguages.EN);
    this.businessMatchingElement.setAttribute('planner-label', this.translatePipe.transform('PLANNER'));
    this.businessMatchingElement.setAttribute('platform', 'WEB');
    this.businessMatchingElement.setAttribute('auth-type', 'WEB');
    this.businessMatchingElement.setAttribute('event-phase', String(event_phase));
    this.businessMatchingElement.setAttribute('action', mfeAction);
    this.businessMatchingElement.setAttribute('is-show-header', 'false');
    this.businessMatchingElement.setAttribute('target-id', this.peopleId());
    if (this.isMeetFromChat()) {
      this.businessMatchingElement.setAttribute('is-meet-from-chat', 'true');
    }
    if (this.isMeetFromChat() || this.globalStateSrv.isMatchmakingImprovements()) {
      const acceptGuidanceContext = mfeAction === 'ACCEPT' ? 'ACCEPT' : null;
      const context = mfeAction === 'REQUEST' ? 'REQUEST' : acceptGuidanceContext;
      this.businessMatchingElement.setAttribute(
        'show-planner-guidance',
        String(context !== null && this.plannerGuidanceSrv.canShow(context))
      );
    }
    if (
      this.globalStateSrv.isExternalMode() &&
      (mfeAction === 'DECLINE' || mfeAction === 'CANCEL' || mfeAction === 'UNDECLINE')
    ) {
      this.businessMatchingElement.setAttribute('hide-go-back', 'true');
    }
    if (!isAnon && customSession) {
      this.businessMatchingElement.setAttribute('auth-token', customSession);
    }
    const customText = this.loadMfeSrv?.customTextBusinessMatching;
    if (customText != null) {
      Object.assign(this.businessMatchingElement, { customText });
    }

    this.businessMatchingElement.addEventListener(
      'businessMatchingListener',
      this.onBusinessMatchingEvent.bind(this) as EventListener
    );

    // Capture attrs before appendChild: connectedCallback on an already-registered
    // element fires synchronously and can dispatch OPEN_CHAT, nulling businessMatchingElement.
    const attrs = getMfeAttributes(this.businessMatchingElement, ['auth-token']);
    this.businessMatchingContainer()?.nativeElement.appendChild(this.businessMatchingElement);
    console.info('BusinessMatchingModule instance added to DOM.');
    this.faroSrv.pushMfeInit('jublia-business-matching-module', attrs);
  }

  /**
   * Top-level dispatcher for all `businessMatchingListener` custom events from the MFE.
   * Branches on `action`; delegates CLOSE and OPEN_CHAT to dedicated handlers,
   * all other CTA actions (REQUEST, ACCEPT, RESCHEDULE, etc.) inline.
   */
  private onBusinessMatchingEvent(event: CustomEvent<BusinessMatchingListenerEvent>): void {
    const {
      action,
      status,
      meetingStatus,
      meetingType,
      availableCta,
      errorCode,
      attendeeName,
      reason,
      decline,
      isFromSuccessScreen,
      plannerGuidanceShown,
      total,
      meeting_location,
      meeting_local_time,
    } = event.detail ?? {};
    if (
      status === 'SUCCESS' &&
      !isFromSuccessScreen &&
      (action === 'REQUEST' || action === 'CREATE' || action === 'ACCEPT' || action === 'RESCHEDULE')
    ) {
      this.successScreenEvent.set(event.detail);
    }
    // Mark guidance as seen when the user clicks Done or Got it on an MFE success screen that showed it.
    // This prevents the host app from showing it again. CREATE uses the same guidance as REQUEST.
    const acceptGuidanceContext = action === 'ACCEPT' ? 'ACCEPT' : null;
    const guidanceContext = action === 'REQUEST' || action === 'CREATE' ? 'REQUEST' : acceptGuidanceContext;
    if (status === 'SUCCESS' && isFromSuccessScreen && plannerGuidanceShown === true && guidanceContext !== null) {
      this.plannerGuidanceSrv.checkAndMark(guidanceContext);
    }
    if (
      this.globalStateSrv.isMatchmakingImprovements() &&
      status === 'SUCCESS' &&
      !isFromSuccessScreen &&
      (action === 'REQUEST' ||
        action === 'CREATE' ||
        action === 'ACCEPT' ||
        action === 'RESCHEDULE' ||
        action === 'REQUEST_REMOVED')
    ) {
      this.activeModal.update({ size: 'md' });
    }
    switch (action) {
      case 'CLOSE':
        if (status === 'SUCCESS' && this.successScreenEvent()) {
          this.dismiss();
          break;
        }
        this.handleCloseEvent({ status, meetingStatus, availableCta, errorCode, attendeeName, reason, total });
        break;

      case 'OPEN_CHAT':
        this.handleOpenChatEvent();
        break;

      // Action completed — close modal, update people card CTA state, show success modal
      case 'REQUEST':
      case 'CREATE':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            if (isFromSuccessScreen) {
              this.globalSrv.showExternalResultModal({
                message: this.translatePipe.transform('REQUEST_SENT_MINI_APP'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
              });
            }
            return;
          }
          this.updatePeopleState({
            action: TypeOfRequestLabel.REQUEST_MEETING,
            meetingStatus,
            availableCta,
            meetingLocation: meeting_location,
            meetingLocalTime: meeting_local_time,
          });
          if (isFromSuccessScreen) {
            this.meetingRequestCompleted.emit();
            this.close();
            if (meetingType !== 'UNSTRUCTURED') {
              this.showPlannerGuidance('REQUEST', attendeeName, () => this.showTimeAvailability(meetingType));
            }
          }
        }
        break;

      case 'ACCEPT':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            if (isFromSuccessScreen) {
              this.globalSrv.showExternalResultModal({
                message: this.translatePipe.transform('MEETING_CONFIRMED'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
              });
              return;
            }
          }

          this.updatePeopleState({
            action: TypeOfRequestLabel.ACCEPT_MEETING,
            meetingStatus,
            availableCta,
            meetingLocation: meeting_location,
            meetingLocalTime: meeting_local_time,
          });
          if (isFromSuccessScreen) {
            this.meetingRequestCompleted.emit();
            this.close();
            if (meetingType !== 'UNSTRUCTURED') {
              this.showPlannerGuidance('ACCEPT', attendeeName, () => this.showTimeAvailability(meetingType));
            }
          }
        }
        break;

      case 'RESCHEDULE':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            if (isFromSuccessScreen) {
              this.globalSrv.showExternalResultModal({
                message: this.translatePipe.transform('MEETING_CONFIRMED'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
              });
            }
            return;
          }
          this.updatePeopleState({
            action: TypeOfRequestLabel.RESCHEDULE_MEETING,
            meetingStatus,
            availableCta,
            meetingLocation: meeting_location,
            meetingLocalTime: meeting_local_time,
          });
          if (isFromSuccessScreen) {
            this.meetingRequestCompleted.emit();
            this.close();
            this.showTimeAvailability(meetingType);
          }
        }
        break;

      case 'REQUEST_REMOVED':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            if (isFromSuccessScreen) {
              this.globalSrv.showExternalResultModal({
                message: this.translatePipe.transform('REQUEST_REMOVED'),
                description: this.translatePipe.transform('CLOSE_WINDOW'),
              });
            }
            return;
          }
          this.updatePeopleState({ action: TypeOfRequestLabel.REMOVE_REQUEST, meetingStatus, availableCta });
          if (isFromSuccessScreen) {
            this.meetingRequestCompleted.emit();
            this.close();
          }
        }
        break;

      case 'CANCEL':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            this.globalSrv.showExternalResultModal({
              message: this.translatePipe.transform('MEETING_CANCELED'),
              description: this.translatePipe.transform('CLOSE_WINDOW'),
            });
            return;
          }
          this.updatePeopleState({
            action: TypeOfRequestLabel.CANCEL_MEETING,
            meetingStatus,
            availableCta,
            meetingLocation: meeting_location,
            meetingLocalTime: meeting_local_time,
          });
          this.close();
        }
        break;

      case 'DECLINE':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            this.globalSrv.showExternalResultModal({
              message: this.translatePipe.transform('MEETING_DECLINED'),
              description: this.translatePipe.transform('CLOSE_WINDOW'),
            });
            return;
          }
          this.close();
          this.updatePeopleState({
            action: TypeOfRequestLabel.DECLINE_HANDSHAKE,
            meetingStatus,
            availableCta,
            decline,
          });
        }
        break;

      case 'UNDECLINE':
        if (status === 'SUCCESS') {
          if (this.globalStateSrv.isExternalMode()) {
            this.globalSrv.showExternalResultModal({
              message: this.translatePipe.transform('DECLINED_REMOVED'),
              description: this.translatePipe.transform('CLOSE_WINDOW'),
            });
            return;
          }
          this.close();
          this.updatePeopleState({
            action: TypeOfRequestLabel.UNDECLINE_HANDSHAKE,
            meetingStatus,
            availableCta,
            decline,
          });
        }
        break;
    }
  }

  /**
   * Handles all CLOSE-action events from the MFE.
   * - SUCCESS          → user manually closed; dismiss the dialog
   * - ERROR            → API failure shown inline; dismiss the dialog
   * - VALIDATION_ERROR → pre-flight check failed; dismiss the dialog and show error modal
   * - PROCEED          → state mismatch; close and navigate to the correct flow
   */
  private handleCloseEvent({
    status,
    meetingStatus,
    availableCta,
    errorCode,
    attendeeName,
    reason,
    total,
  }: {
    status: BusinessMatchingListenerEventStatus;
    meetingStatus: BusinessMatchingMeetingStatus | undefined;
    availableCta: BusinessMatchingCtaAction[] | undefined;
    errorCode: string | undefined;
    attendeeName: string | undefined;
    reason: string | undefined;
    total: number | null | undefined;
  }): void {
    switch (status) {
      case 'SUCCESS':
      case 'ERROR':
        this.close();
        break;

      case 'VALIDATION_ERROR':
        this.close();
        this.showValidationErrorModal(errorCode, attendeeName, total);
        break;

      case 'PROCEED':
        this.handleProceedNavigation({ meetingStatus, availableCta, attendeeName, reason });
        break;
    }
  }

  /**
   * Translates `errorCode` to a user-facing string and opens the failed-request modal.
   * Interpolates `name` and `total` when present (e.g. MAX_HANDSHAKE_REACHED shows attendeeName + count).
   */
  private showValidationErrorModal(errorCode: string | undefined, attendeeName?: string, total?: number | null): void {
    if (!errorCode) return;
    const validationErrorCode = errorCode as BusinessMatchingValidationErrorCode;
    // Map is total over known codes; the fallback guards against codes the MFE adds later.
    const key: string =
      BUSINESS_MATCHING_VALIDATION_ERROR_DESCRIPTION_MAP[validationErrorCode] ?? 'SOMETHING_WENT_WRONG';
    const header = BUSINESS_MATCHING_VALIDATION_ERROR_TITLE_MAP[validationErrorCode] ?? 'HOLD_ON';
    const params: Record<string, string | number> = {};
    if (attendeeName) params['name'] = attendeeName;
    if (total != null) params['total'] = total;
    const description = Object.keys(params).length ? this.translatePipe.transform(key, params) : key;
    this.modalSrv.showModalFailedRequest({ addsData: { header, description } });
  }

  /**
   * Handles PROCEED navigation: the MFE detected a state mismatch and the host
   * must route the user to the appropriate next flow.
   *
   * - RECEIVED_A_MEET          → user has an incoming request; re-open in ACCEPT mode
   * - PENDING_MEETING          → meeting already scheduled; re-open in RESCHEDULE mode
   * - STRUCTURED_MEETING       → meeting already scheduled; re-open in RESCHEDULE mode
   * - OTHER_DECLINE/USER_DECLINE → meeting was declined; close and update card state
   */
  private handleProceedNavigation({
    meetingStatus,
    availableCta,
    attendeeName,
    reason,
  }: {
    meetingStatus: BusinessMatchingMeetingStatus | undefined;
    availableCta: BusinessMatchingCtaAction[] | undefined;
    attendeeName: string | undefined;
    reason: string | undefined;
  }): void {
    switch (meetingStatus) {
      case 'RECEIVED_A_MEET':
        this.destroyBusinessMatching();
        this.modalSrv.closeAll();
        this.modalSrv.showModalRequestMeet({
          addsData: {
            peopleId: this.peopleId(),
            action: TypeOfRequestLabel.ACCEPT_MEETING,
            isMeetFromChat: this.isMeetFromChat(),
          },
        });
        break;

      case 'PENDING_MEETING':
      case 'STRUCTURED_MEETING':
        this.destroyBusinessMatching();
        this.modalSrv.closeAll();
        this.modalSrv.showModalRequestMeet({
          addsData: {
            peopleId: this.peopleId(),
            action: TypeOfRequestLabel.RESCHEDULE_MEETING,
            isReschedule: true,
            isMeetFromChat: this.isMeetFromChat(),
          },
        });
        break;

      case 'OTHER_DECLINE': {
        this.close();
        if (availableCta) {
          this.updatePeopleState({ action: TypeOfRequestLabel.DECLINE_HANDSHAKE, meetingStatus, availableCta });
        }
        this.modalSrv.showModalInformation({
          addsData: {
            title: this.translatePipe.transform('HOLD_ON'),
            message: this.translatePipe.transform('HAS_DECLINED_YOUR_MEETING_REQUEST_BECAUSE:', {
              name: attendeeName ?? '',
              reason: reason ?? '',
            }),
          },
        });
        break;
      }

      case 'USER_DECLINE':
        this.close();
        if (availableCta) {
          this.updatePeopleState({
            action: TypeOfRequestLabel.DECLINE_HANDSHAKE,
            meetingStatus,
            availableCta,
            decline: TypeOfDecline.USER_DECLINE,
          });
        }
        break;

      default:
        this.close();
        break;
    }
  }

  /**
   * Handles OPEN_CHAT: the MFE determined that an unstructured (chat) meeting already
   * exists — close all modals and open the chat view directly.
   */
  private handleOpenChatEvent(): void {
    this.destroyBusinessMatching();
    this.modalSrv.closeAll();
    this.modalSrv.showModalChat({ addsData: { peopleId: this.peopleId() } });
  }

  private showPlannerGuidance(
    context: Extract<PlannerGuidanceContext, 'REQUEST' | 'ACCEPT'>,
    attendeeName?: string,
    afterClose?: () => void
  ): void {
    if (
      this.globalStateSrv.isExternalMode() ||
      this.globalStateSrv.isMatchmakingImprovements() ||
      !this.plannerGuidanceSrv.checkAndMark(context)
    ) {
      afterClose?.();
      return;
    }

    try {
      const modalRef = this.modalSrv.showModalPlannerGuidance({
        addsData: {
          context,
          attendeeName: attendeeName ?? '',
        },
      });
      if (!modalRef) {
        this.plannerGuidanceSrv.rollback(context);
        afterClose?.();
      } else if (afterClose) {
        // The follow-up belongs to this modal and survives the matching component's teardown.
        modalRef.hidden.pipe(take(1)).subscribe(afterClose);
      }
    } catch {
      this.plannerGuidanceSrv.rollback(context);
      console.error('Error showing planner guidance:', context);
      afterClose?.();
    }
  }

  /**
   * Propagates a successful MFE action to the host state so that people cards
   * reflect the updated CTA list without a full page refetch.
   * `meetingStatus` is included in `newValue` so that `initValue()` recomputes
   * CTAs from the new status rather than the stale one.
   */
  private updatePeopleState({
    action,
    meetingStatus,
    availableCta,
    decline,
    meetingLocation,
    meetingLocalTime,
  }: {
    action: TypeOfRequestLabel;
    meetingStatus: BusinessMatchingMeetingStatus | undefined;
    availableCta: BusinessMatchingCtaAction[] | undefined;
    decline?: BusinessMatchingDeclineStatus | 'OTHER_DECLINE';
    meetingLocation?: string | null;
    meetingLocalTime?: string | null;
  }): void {
    if (availableCta === undefined) return;
    // The MFE reports decline outcomes as meetingStatus values. Store them in `declined`,
    // never in `meeting_status` — computeAvailableCta has no case for them and would
    // yield an empty CTA list on the cards.
    if (meetingStatus === 'OTHER_DECLINE' || meetingStatus === 'USER_DECLINE') {
      if (decline === undefined) decline = meetingStatus;
      meetingStatus = undefined;
    }
    const newValue: Record<string, unknown> = { available_cta: availableCta };
    if (meetingStatus) newValue['meeting_status'] = meetingStatus;
    if (decline) newValue['declined'] = decline;
    if (meetingLocation !== undefined) newValue['object_loc'] = meetingLocation ?? '';
    if (meetingLocalTime !== undefined) {
      const parts = (meetingLocalTime ?? '').split(', ');
      newValue['local_time'] = parts[0] ?? '';
      newValue['local_date'] = parts[1] ?? '';
    }
    this.requestLabelSrv.setEventUpdated({
      status: true,
      data: {
        id: this.peopleId(),
        action,
        newValue,
      },
    });
  }

  /**
   * Shows time availability after the Planner success/guidance screen is dismissed.
   */
  private showTimeAvailability(meetingType: BusinessMatchingMeetingType | undefined): void {
    const shouldShowTimeAvailability =
      !this.globalStateSrv.isTimeslotConfirmed() &&
      !this.globalStateSrv.isAnon() &&
      meetingType !== 'UNSTRUCTURED' &&
      !this.globalStateSrv.isExternalMode();

    if (!shouldShowTimeAvailability) {
      return;
    }

    const modalId = 'modal-time-availability';
    const config: ModalConfig = {
      ...defaultModalConfig,
      size: 'xl',
      modalId,
      backdrop: 'static',
      keyboard: false,
    };

    this.modalSrv.open(ModalTimeAvailabilityComponent, { modalId }, config);
  }

  /**
   * Removes the `<jublia-business-matching-module>` element from the DOM and clears the local ref.
   * Idempotent — safe to call multiple times or when no element exists.
   * Pushes a Faro destroy event for observability before removal.
   */
  private destroyBusinessMatching(): void {
    if (
      this.businessMatchingElement &&
      this.businessMatchingContainer()?.nativeElement.contains(this.businessMatchingElement)
    ) {
      const attrs = getMfeAttributes(this.businessMatchingElement, ['auth-token']);
      this.businessMatchingContainer()?.nativeElement.removeChild(this.businessMatchingElement);
      console.info('BusinessMatchingModule instance removed from DOM.');
      this.faroSrv.pushMfeDestroy('jublia-business-matching-module', attrs);
    }
    this.businessMatchingElement = null;
  }
}
